import React, { useEffect, useState } from 'react';
import { Input } from 'baseui/input';
import { Button, KIND } from 'baseui/button';
import { applyAuthTokensFromResponse } from '../../utils/authTokenStore';
import { mfaService } from '../shared/hooks/AuthService';
import './SecuritySettingsPanel.css';

export default function SecuritySettingsPanel({ onMessage }) {
  const [enabled, setEnabled] = useState(false);
  const [eligible, setEligible] = useState(true);
  const [googleSignInOnly, setGoogleSignInOnly] = useState(false);
  const [authProvider, setAuthProvider] = useState('LOCAL');
  const [setup, setSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disablePwd, setDisablePwd] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    try {
      const res = await mfaService.status();
      setEnabled(!!res.data?.enabled);
      setEligible(res.data?.eligible !== false);
      setGoogleSignInOnly(!!res.data?.googleSignInOnly);
      setAuthProvider(res.data?.authProvider || 'LOCAL');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const startSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await mfaService.setup();
      setSetup(res.data);
      onMessage?.('Scan the QR code with your authenticator app.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start MFA setup.');
    } finally {
      setLoading(false);
    }
  };

  const enableMfa = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await mfaService.enable(confirmCode);
      applyAuthTokensFromResponse(res.data);
      setSetup(null);
      setConfirmCode('');
      setEnabled(true);
      onMessage?.('Two-factor authentication enabled.');
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const disableMfa = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await mfaService.disable(disablePwd, disableCode);
      setEnabled(false);
      setDisablePwd('');
      setDisableCode('');
      onMessage?.('Two-factor authentication disabled.');
      if (res.data?.requiresReLogin) {
        onMessage?.('Sign in again with your password.');
        window.location.href = '/login';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not disable MFA.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="security-panel">
      <section className="security-section">
        <h2>Two-factor authentication (TOTP)</h2>
        <p className="security-muted">
          Protect email and password sign-in with a 6-digit code from an authenticator app. When enabled,
          you&apos;ll enter a code at login and again when this browser session needs verification.
        </p>
        <div className="security-status-row">
          <span>
            Status:{' '}
            <span className={`security-badge ${enabled ? 'security-badge--on' : 'security-badge--off'}`}>
              {enabled ? 'Enabled' : 'Not enabled'}
            </span>
          </span>
          {googleSignInOnly && (
            <span className="security-badge security-badge--google">Google sign-in</span>
          )}
          {!googleSignInOnly && authProvider === 'BOTH' && (
            <span className="security-badge security-badge--google">Google + password</span>
          )}
        </div>

        {googleSignInOnly && (
          <p className="security-muted">
            You sign in with Google only. TOTP is not required for Google sessions — manage account security in
            your Google account. Set a password under Profile to also sign in with email and enable MFA here.
          </p>
        )}

        {!googleSignInOnly && !enabled && !setup && eligible && (
          <Button onClick={startSetup} isLoading={loading}>
            Set up authenticator
          </Button>
        )}

        {setup && (
          <div className="security-setup-card">
            <img src={setup.qrCodeDataUrl} alt="MFA QR code" width={180} height={180} />
            <p className="security-secret">Manual key: {setup.secret}</p>
            <div className="security-field">
              <Input
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="Enter 6-digit code"
              />
            </div>
            <div className="security-actions">
              <Button onClick={enableMfa} isLoading={loading} disabled={confirmCode.length < 6}>
                Confirm &amp; enable
              </Button>
              <Button kind={KIND.tertiary} onClick={() => setSetup(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {enabled && !googleSignInOnly && (
          <>
            <p className="security-muted" style={{ marginBottom: '0.75rem' }}>
              To disable MFA, enter your current password and a valid authenticator code.
            </p>
            <div className="security-field">
              <Input
                type="password"
                value={disablePwd}
                onChange={(e) => setDisablePwd(e.target.value)}
                placeholder="Current password"
              />
            </div>
            <div className="security-field">
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="Authenticator code"
              />
            </div>
            <Button kind={KIND.secondary} onClick={disableMfa} isLoading={loading}>
              Disable MFA
            </Button>
          </>
        )}

        {error && (
          <p className="security-error" role="alert">
            {error}
          </p>
        )}
      </section>

      <section className="security-section">
        <h2>How sign-in works</h2>
        <ul className="security-list">
          <li>Email and password: MFA code required at each login when enabled.</li>
          <li>Google sign-in: no TOTP prompt — Google verifies your identity.</li>
          <li>Sessions use HttpOnly cookies and rotating refresh tokens on this device.</li>
          <li>Admin and billing APIs are authorized on the server for every request.</li>
        </ul>
      </section>
    </div>
  );
}
